"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDragAndDrop = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
const index_js_1 = require("../functions/index.js");
const useEventListener_js_1 = require("./useEventListener.js");
const useResizeObserver_js_1 = require("./useResizeObserver.js");
const getContainerRect = (containerRef) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const containerRect = (_a = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
    return {
        top: (_b = containerRect === null || containerRect === void 0 ? void 0 : containerRect.top) !== null && _b !== void 0 ? _b : 0,
        right: (_e = (_c = containerRect === null || containerRect === void 0 ? void 0 : containerRect.right) !== null && _c !== void 0 ? _c : (_d = (0, index_js_1.getWindow)()) === null || _d === void 0 ? void 0 : _d.innerWidth) !== null && _e !== void 0 ? _e : 0,
        bottom: (_h = (_f = containerRect === null || containerRect === void 0 ? void 0 : containerRect.bottom) !== null && _f !== void 0 ? _f : (_g = (0, index_js_1.getWindow)()) === null || _g === void 0 ? void 0 : _g.innerHeight) !== null && _h !== void 0 ? _h : 0,
        left: (_j = containerRect === null || containerRect === void 0 ? void 0 : containerRect.left) !== null && _j !== void 0 ? _j : 0,
    };
};
/**
 * Helper hook that handles elements drag logic.
 * @param elementRef Element ref that is draggable.
 * @param containerRect Bounding rectangle of container element that element can't go outside. If not passed window is used as a container.
 * @returns
 * `onPointerDown` - handler that is called when pointer is down and handles all the dragging logic.
 * `transform` - current transform of the element, it is used to preserve drag position when element visibility is being toggled.
 */
const useDragAndDrop = (elementRef, containerRef, enabled = true) => {
    const grabOffsetX = React.useRef(0);
    const grabOffsetY = React.useRef(0);
    const translateX = React.useRef();
    const translateY = React.useRef();
    const containerRectRef = React.useRef(getContainerRect(containerRef));
    const adjustTransform = React.useCallback(() => {
        var _a;
        if (!elementRef.current || !enabled) {
            return;
        }
        const { top, right, bottom, left } = (_a = elementRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        let [newTranslateX, newTranslateY] = (0, index_js_1.getTranslateValues)(elementRef.current);
        containerRectRef.current = getContainerRect(containerRef);
        if (bottom > containerRectRef.current.bottom) {
            newTranslateY -= bottom - containerRectRef.current.bottom;
        }
        if (top < containerRectRef.current.top) {
            newTranslateY += containerRectRef.current.top - top;
        }
        if (right > containerRectRef.current.right) {
            newTranslateX -= right - containerRectRef.current.right;
        }
        if (left < containerRectRef.current.left) {
            newTranslateX += containerRectRef.current.left - left;
        }
        translateX.current = newTranslateX;
        translateY.current = newTranslateY;
        elementRef.current.style.transform = `translate(${newTranslateX}px, ${newTranslateY}px)`;
    }, [containerRef, elementRef, enabled]);
    const [resizeRef, resizeObserver] = (0, useResizeObserver_js_1.useResizeObserver)(adjustTransform);
    resizeRef(containerRef === null || containerRef === void 0 ? void 0 : containerRef.current);
    React.useEffect(() => {
        return () => {
            resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
        };
    }, [resizeObserver]);
    (0, useEventListener_js_1.useEventListener)('resize', () => {
        adjustTransform();
        if (translateX.current != null && translateY.current != null) {
            setTransform(`translate(${translateX.current}px, ${translateY.current}px)`);
        }
    }, (0, index_js_1.getWindow)());
    const [transform, setTransform] = React.useState('');
    const onPointerMove = React.useRef((event) => {
        if (!elementRef.current) {
            return;
        }
        const newTranslateX = event.clientX - grabOffsetX.current;
        const newTranslateY = event.clientY - grabOffsetY.current;
        elementRef.current.style.transform = `translate(${newTranslateX}px, ${newTranslateY}px)`;
        adjustTransform();
    });
    const originalUserSelect = React.useRef('');
    const onPointerDown = React.useCallback((e) => {
        if (!elementRef.current || e.button !== 0 || !enabled) {
            return;
        }
        const [x, y] = (0, index_js_1.getTranslateValues)(elementRef.current);
        grabOffsetX.current = e.clientX - x;
        grabOffsetY.current = e.clientY - y;
        originalUserSelect.current = elementRef.current.style.userSelect;
        // Prevents from selecting inner content when dragging.
        elementRef.current.style.userSelect = 'none';
        const ownerDocument = elementRef.current.ownerDocument || document;
        ownerDocument.addEventListener('pointermove', onPointerMove.current);
        ownerDocument.addEventListener('pointerup', () => {
            setTransform(`translate(${translateX.current}px, ${translateY.current}px)`);
            ownerDocument.removeEventListener('pointermove', onPointerMove.current);
            if (elementRef.current) {
                elementRef.current.style.userSelect = originalUserSelect.current;
            }
        }, { once: true });
    }, [elementRef, enabled]);
    return { onPointerDown, transform };
};
exports.useDragAndDrop = useDragAndDrop;
exports.default = exports.useDragAndDrop;
